import { useCallback, useMemo } from 'react';
import createContext from 'constate';
import { formatWithCurrency } from '../utils/format';
import {
  useQueryDigitalBillFeatureFlag,
  useQueryFailedPaymentPromptFeatureFlag,
} from '../hooks/useFeatureFlagApi';
import type { LocationDtoV3 } from '../../generated-api';

interface Props {
  location: LocationDtoV3;
}

const [LocationContextProvider, useLocation] = createContext(({ location }: Props) => {
  const { data: digitalBillFeatureFlag } = useQueryDigitalBillFeatureFlag(location.id);
  const { data: failedPaymentPromptFeatureFlag } = useQueryFailedPaymentPromptFeatureFlag(location.locationId);

  const isDigitalBillEnabled = Boolean(digitalBillFeatureFlag?.waiterCanInitiate);

  const formatCurrency = useCallback(
    (amount: number) => (location ? formatWithCurrency(amount, location.currency) : ''),
    [location]
  );

  const counterDeliveryMethod = useMemo(
    () => location.deliveryMethods.find((d) => d.type === 'COUNTER'),
    [location.deliveryMethods]
  );

  const featureFlags = useMemo(
    () => ({
      digitalBillFeatureFlag,
      failedPaymentPromptFeatureFlag,
    }),
    [digitalBillFeatureFlag, failedPaymentPromptFeatureFlag]
  );

  return useMemo(
    () => ({
      counterDeliveryMethod,
      featureFlags,
      formatCurrency,
      isDigitalBillEnabled,
      location,
    }),
    [location, formatCurrency, isDigitalBillEnabled, counterDeliveryMethod, featureFlags]
  );
});

export { LocationContextProvider, useLocation };
